/**
 * ContentPlannerLayout — master shell for the Content Planner module.
 *
 *   • Persistent left sidebar (PlannerSidebar) with cross-portal nav
 *   • Slim sticky top bar (PlannerTopBar) with breadcrumb + page title
 *   • Cream canvas matching the marketing site
 *   • A scoped <style> block that lifts every inline-styled tile inside
 *     the planner to match the rest of the site's .hv-card system —
 *     rounded 12 px, soft drop shadow, cyan-glow hover on draft cards.
 *     One source of truth, no per-page patching.
 *
 *   • Mobile (≤ 1024 px): sidebar becomes an off-canvas drawer; a
 *     hamburger in the top bar toggles it; the main content reclaims
 *     the full viewport width; a backdrop closes the drawer on tap.
 */
import React, { useEffect, useState } from "react";
import PlannerSidebar, { STORAGE_COLLAPSED, plannerTheme } from "./PlannerSidebar";
import PlannerTopBar from "./PlannerTopBar";

const MOBILE_BREAKPOINT = 1024; // px — admin tools need a bit more room than 768

function readCollapsed() {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(STORAGE_COLLAPSED) === "1";
}

function readIsMobile() {
  if (typeof window === "undefined") return false;
  return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`).matches;
}

export default function ContentPlannerLayout({ children, topBarActions }) {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [isMobile, setIsMobile]   = useState(readIsMobile);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Mirror localStorage so changes from anywhere in the planner reflect here.
  useEffect(() => {
    let last = readCollapsed();
    const id = setInterval(() => {
      const now = readCollapsed();
      if (now !== last) { last = now; setCollapsed(now); }
    }, 150);
    return () => clearInterval(id);
  }, []);

  // Track viewport breakpoint live so the sidebar adapts on rotate / resize.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const onChange = (e) => {
      setIsMobile(e.matches);
      if (!e.matches) setMobileOpen(false); // closing the drawer on resize-up
    };
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  // Lock body scroll while the mobile drawer is open so the page behind
  // doesn't scroll under the drawer.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (isMobile && mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
    return undefined;
  }, [isMobile, mobileOpen]);

  const t = plannerTheme(false);
  const desktopSidebarWidth = collapsed ? 72 : 260;
  // Off-canvas on mobile → main content takes the full viewport.
  const contentOffset = isMobile ? 0 : desktopSidebarWidth;

  return (
    <div
      data-testid="content-planner-layout"
      style={{
        minHeight: "100vh",
        background: t.canvasBg,
        color: t.topBarText,
        fontFamily: "Inter, IBM Plex Sans, system-ui, sans-serif",
      }}
    >
      {/* Coherent tile system — promotes every inline-styled white /
          cream surface inside the planner content area to a soft,
          rounded, drop-shadowed tile that matches the rest of the
          HidroVerse site (.hv-card visual language).  We target by
          background colour so we don't have to touch every page. */}
      <style>{`
        [data-testid="planner-content"] [style*="background: #FFFFFF"],
        [data-testid="planner-content"] [style*="background:#FFFFFF"] {
          border-radius: 12px !important;
          box-shadow: 0 1px 2px rgba(12,42,64,0.04),
                      0 10px 28px -16px rgba(12,42,64,0.10) !important;
          transition: transform 240ms cubic-bezier(0.2,0.7,0.2,1),
                      box-shadow 240ms cubic-bezier(0.2,0.7,0.2,1),
                      border-color 240ms cubic-bezier(0.2,0.7,0.2,1) !important;
        }
        /* Promote planner's hv-card-soft and hv-card panels — give them
           the same lift as the rest of the white tiles so they stand
           apart from the cream canvas. */
        [data-testid="planner-content"] .hv-card-soft {
          background: #FFFFFF !important;
          border: 1px solid rgba(12,42,64,0.08) !important;
          border-radius: 12px !important;
          box-shadow: 0 1px 2px rgba(12,42,64,0.04),
                      0 10px 28px -16px rgba(12,42,64,0.10) !important;
        }
        [data-testid="planner-content"] .hv-card {
          border-radius: 12px !important;
        }
        [data-testid="planner-content"] [style*="background: #FAFAF7"],
        [data-testid="planner-content"] [style*="background:#FAFAF7"],
        [data-testid="planner-content"] [style*="background: #FCFCFA"],
        [data-testid="planner-content"] [style*="background:#FCFCFA"] {
          border-radius: 10px !important;
        }
        [data-testid="planner-content"] [style*="background: #F3FBFB"],
        [data-testid="planner-content"] [style*="background:#F3FBFB"] {
          border-radius: 12px !important;
          box-shadow: 0 1px 2px rgba(12,42,64,0.04),
                      0 14px 32px -20px rgba(31,182,193,0.30) !important;
        }
        /* Inputs / textareas / selects — match .hv-input rhythm */
        [data-testid="planner-content"] input:not([type="checkbox"]):not([type="radio"]),
        [data-testid="planner-content"] textarea,
        [data-testid="planner-content"] select {
          border-radius: 8px !important;
          transition: border-color 180ms ease, box-shadow 180ms ease, background 180ms ease !important;
        }
        [data-testid="planner-content"] input:not([type="checkbox"]):not([type="radio"]):focus,
        [data-testid="planner-content"] textarea:focus,
        [data-testid="planner-content"] select:focus {
          border-color: rgba(31,182,193,0.55) !important;
          background: #FFFFFF !important;
          box-shadow: 0 0 0 4px rgba(31,182,193,0.10) !important;
        }
        /* Pills and buttons with rounded inline radius keep their look —
           we only round the unstyled ones up to 8 px. */
        [data-testid="planner-content"] button:not([style*="border-radius"]):not([style*="borderRadius"]) {
          border-radius: 8px;
        }
        /* Tables — soft outer border + rounded corners */
        [data-testid="planner-content"] table {
          border-radius: 12px;
          overflow: hidden;
        }
        /* Stat tiles and draft cards: gentle lift on hover */
        [data-testid="planner-content"] [data-testid^="cc-stat-"]:hover,
        [data-testid="planner-content"] [data-testid^="cc-source-row-"]:hover {
          border-color: rgba(31,182,193,0.30) !important;
        }
        /* ───────── Mobile-specific overrides ─────────
           Wide grids that were laid out for 1280-px desktop tend to
           overflow on phones. Force every grid inside the planner to
           collapse to a single column on small screens, and stop wide
           fixed-width buttons / inputs from punching through the
           viewport. */
        @media (max-width: 1023px) {
          [data-testid="planner-content"] { padding: 18px 16px 56px !important; }
          [data-testid="planner-content"] .grid { gap: 12px !important; }
          [data-testid="planner-content"] [class*="md:grid-cols-"],
          [data-testid="planner-content"] [class*="lg:grid-cols-"],
          [data-testid="planner-content"] [class*="xl:grid-cols-"] {
            grid-template-columns: minmax(0, 1fr) !important;
          }
          [data-testid="planner-content"] [style*="min-width"] { min-width: 0 !important; }
          [data-testid="planner-content"] input,
          [data-testid="planner-content"] textarea,
          [data-testid="planner-content"] select { max-width: 100% !important; }
          [data-testid="planner-content"] table { display: block; overflow-x: auto; }
          [data-testid="planner-topbar"] { padding: 0 16px !important; }
          [data-testid="planner-topbar-title"] { font-size: 17px !important; }
        }
        @keyframes hvFade { from { opacity: 0 } to { opacity: 1 } }
      `}</style>

      <PlannerSidebar
        isMobile={isMobile}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />
      <PlannerTopBar
        sidebarWidth={contentOffset}
        bg={t.topBarBg}
        text={t.topBarText}
        muted={t.topBarMuted}
        border={t.topBarBorder}
        isMobile={isMobile}
        onMenuClick={() => setMobileOpen(true)}
      >
        {topBarActions}
      </PlannerTopBar>
      <main
        data-testid="planner-content"
        style={{
          marginLeft: contentOffset,
          transition: "margin-left 0.25s cubic-bezier(0.4,0,0.2,1)",
          minHeight: "calc(100vh - 64px)",
          padding: "32px clamp(20px, 4vw, 48px) 64px",
        }}
      >
        {children}
      </main>
    </div>
  );
}
